import type { Types } from '@cornerstonejs/core';
import { metaData } from '@cornerstonejs/core';
import { annotation } from '@cornerstonejs/tools';
import type { AppDispatch } from '@/state/store';
import type { Measurement } from '@/models/report';
import { SPECTRAL_DOPPLER_TOOL_NAMES } from '@/models/spectralDoppler';
import { mapAnnotationToMeasurement } from '@/state/slices/measurementSession';
import {
  resolveSpectralRegion,
  buildCalibrationFromRegion,
} from '../../tools/SpectralDoppler/calibrationRegion';
import type { UltrasoundRegionsMeta } from '../../tools/SpectralDoppler/SpectralDopplerBaseTool';
import { getBaseAnnotationStyle } from '../../utils/annotationStyle';
import { hydratedAnnotationUid } from '../../utils/annotationUid';
import { resolveDopplerImageId } from './useAnnotationHydration.aiDoppler';

interface HydrateProbeParams {
  measurement: Measurement;
  studyUuid: string;
  dicomUuid: string;
  frameOfReferenceUID: string;
  viewport: {
    element: HTMLDivElement;
    getCamera: () => Types.ICamera;
    getCurrentImageId?: () => string | undefined;
  };
  label: string;
  /** Worksheet abbreviation (e.g. "MV E") — powers the hover chip + header. */
  abbreviation?: string;
  /** Keep the selected style if this probe was selected before it hydrated. */
  isSelected?: boolean;
  canEdit: boolean;
  dispatch: AppDispatch;
}

/** The probe stores its velocity in `spectralValues` the same way a Length
 *  stores its length in `cachedStats`. The persisted primary value is peakV in
 *  the row's unit (m/s or cm/s); seed it so the label renders without needing
 *  the live calibration. */
function seedProbeValues(m: Measurement): { peakV_cmS: number } | undefined {
  if (m.value == null) return undefined;
  if (m.unit === 'm/s') return { peakV_cmS: m.value * 100 };
  if (m.unit === 'cm/s') return { peakV_cmS: m.value };
  return undefined;
}

/**
 * Re-paints a single persisted POINT annotation as a native VelocityProbeTool
 * annotation so the crosshair (+ velocity label) reappear after a hard refresh.
 *
 * Mirrors the Length/Angle/Freehand hydration branch: rebuilds the annotation
 * from the persisted point, seeds the velocity from the stored value, and (best
 * effort) rebuilds the region calibration so dragging the crosshair recomputes.
 * Returns true if added, false if it already exists.
 */
export function hydrateProbeAnnotation({
  measurement: m,
  studyUuid,
  dicomUuid,
  frameOfReferenceUID,
  viewport,
  label,
  abbreviation,
  isSelected,
  canEdit,
  dispatch,
}: HydrateProbeParams): boolean {
  const ann = m.annotation!;
  const uid = hydratedAnnotationUid(studyUuid, m.canonicalMeasurement);

  if (annotation.state.getAnnotation(uid)) return false;

  // referencedImageId must equal what the viewport reports as its current
  // imageId, or filterInteractableAnnotationsForElement drops the annotation.
  // Shared with the AI probe path — see resolveDopplerImageId for the
  // stack-still vs video-sweep anchoring rules.
  const referencedImageId = resolveDopplerImageId(
    viewport,
    dicomUuid,
    ann.frameNumber
  );
  // Rebuild the region calibration from viewport metadata (same lookup the tool
  // uses when drawing) so dragging the crosshair re-derives velocity. Best
  // effort: if regions aren't available yet the probe still hydrates (crosshair
  // + seeded value) and simply won't recompute on drag — never a regression to
  // the current traceability.
  const imageId = viewport.getCurrentImageId?.() ?? '';
  const regionsMeta = metaData.get('ultrasoundRegions', imageId) as
    | UltrasoundRegionsMeta
    | undefined;
  const { region } = resolveSpectralRegion(regionsMeta?.regions);
  const spectralCalibration = region
    ? buildCalibrationFromRegion(region)
    : undefined;

  const camera = viewport.getCamera();
  const worldPoint = [...ann.points[0]] as Types.Point3;

  annotation.state.addAnnotation(
    {
      annotationUID: uid,
      highlighted: false,
      autoGenerated: false,
      invalidated: false,
      isLocked: !canEdit,
      isVisible: true,
      metadata: {
        toolName: SPECTRAL_DOPPLER_TOOL_NAMES.PROBE,
        FrameOfReferenceUID: frameOfReferenceUID,
        referencedImageId,
        viewPlaneNormal: [0, 0, 1] as Types.Point3,
        viewUp: camera.viewUp ?? ([0, -1, 0] as Types.Point3),
        cameraFocalPoint: camera.focalPoint ?? worldPoint,
        cameraPosition: camera.position ?? ([0, 0, 0] as Types.Point3),
      },
      data: {
        handles: {
          points: [worldPoint],
          activeHandleIndex: null,
          textBox: {
            hasMoved: false,
            worldPosition: worldPoint,
            worldBoundingBox: {
              topLeft: worldPoint,
              topRight: worldPoint,
              bottomLeft: worldPoint,
              bottomRight: worldPoint,
            },
          },
        },
        label: '',
        measurementLabel: label,
        abbreviation,
        // Human probe → manual (blue); mirrors isAI:false on the Length path.
        isAI: false,
        spectralToolVariant: 'probe',
        spectralValues: seedProbeValues(m),
        spectralCalibration,
      },
    },
    viewport.element
  );

  // Cornerstone gates endpoint drag via annotation.locking — this call is
  // load-bearing even though isLocked is also set on the annotation object.
  annotation.locking.setAnnotationLocked(uid, !canEdit);

  // Match the Length/volume hydration: style through the shared source-color
  // palette so hover/selection behave consistently instead of the always-on
  // Cornerstone default (which vanished on first hover — see AP-695).
  annotation.config.style.setAnnotationStyles(
    uid,
    getBaseAnnotationStyle(
      { data: { isAI: false } },
      { isSelected: !!isSelected }
    )
  );

  dispatch(
    mapAnnotationToMeasurement({
      annotationUID: uid,
      canonicalMeasurement: m.canonicalMeasurement,
    })
  );

  return true;
}
